import {
  formatJstDateTime,
  instantFromEpochMilliseconds,
} from "@nijiurachan/js/util/time";
import { useState } from "react";
import { FiX } from "react-icons/fi";
import { useNgStore } from "@/features/ng-filter/stores";
import { SettingRow, SettingSection } from "@/features/settings/ui";
import { Button, HashBitmap, Input, Toggle } from "@/shared/ui/form";
import { TextLink } from "@/shared/ui/navigation";

type InputChangeEvent = React.ChangeEvent<HTMLInputElement, HTMLInputElement>;

/**
 * NG設定タブのコンテンツ
 */
export const NgSettings: React.FunctionComponent = () => {
  const {
    enabled,
    showNgContent,
    hiddenThreadIds,
    ngDisplayIds,
    ngTitles,
    ngWords,
    ngRegexes,
    ngImages,
    setEnabled,
    setShowNgContent,
    unhideThread,
    addNgDisplayId,
    removeNgDisplayId,
    addNgTitle,
    removeNgTitle,
    addNgWord,
    removeNgWord,
    addNgRegex,
    removeNgRegex,
    addNgImage,
    removeNgImage,
    clearNgImages,
  } = useNgStore();

  const [ngDisplayIdInput, setNgDisplayIdInput] = useState("");
  const [ngTitleInput, setNgTitleInput] = useState("");
  const [ngWordInput, setNgWordInput] = useState("");
  const [ngRegexInput, setNgRegexInput] = useState("");
  const [ngImageInput, setNgImageInput] = useState("");
  const [ngImageNoteInput, setNgImageNoteInput] = useState("");

  const handleAddNgDisplayId = (): void => {
    const displayId = ngDisplayIdInput.trim();
    if (!displayId) {
      alert("表示IDを入力してください");
      return;
    }
    addNgDisplayId(displayId);
    setNgDisplayIdInput("");
  };

  const handleAddNgTitle = (): void => {
    const title = ngTitleInput.trim();
    if (!title) {
      alert("NGスレ文を入力してください");
      return;
    }
    addNgTitle(title);
    setNgTitleInput("");
  };

  const handleAddNgWord = (): void => {
    const word = ngWordInput.trim();
    if (!word) {
      alert("NGワードを入力してください");
      return;
    }
    addNgWord(word);
    setNgWordInput("");
  };

  const handleAddNgRegex = (): void => {
    const regex = ngRegexInput.trim();
    if (!regex) {
      alert("正規表現パターンを入力してください");
      return;
    }
    // 正規表現の妥当性チェックはaddNgRegex内で行われる
    try {
      new RegExp(regex);
      addNgRegex(regex);
      setNgRegexInput("");
    } catch {
      alert("無効な正規表現パターンです");
    }
  };

  const handleAddNgImage = (): void => {
    const hash = ngImageInput.trim();
    if (!hash) {
      alert("画像ハッシュを入力してください");
      return;
    }
    const result = addNgImage(hash, ngImageNoteInput);
    alert(result.message);
    if (result.success) {
      setNgImageInput("");
      setNgImageNoteInput("");
    }
  };

  return (
    <>
      <SettingSection title="NG表示ID" description="投稿者の表示IDで非表示">
        <SettingRow>
          <Input
            type="text"
            placeholder="表示ID（例: ABC123）"
            className="w-full"
            value={ngDisplayIdInput}
            onChange={(e: InputChangeEvent): void =>
              setNgDisplayIdInput(e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent): void => {
              if (e.key === "Enter") {
                handleAddNgDisplayId();
              }
            }}
          />
          <Button variant="primary" onClick={handleAddNgDisplayId}>
            追加
          </Button>
        </SettingRow>
        {ngDisplayIds.length > 0 ? (
          <ul aria-label="NG表示ID" className="m-0 list-none space-y-2 p-0">
            {ngDisplayIds.map(({ id: displayId }) => (
              <li key={displayId}>
                <SettingRow>
                  <data
                    value={displayId}
                    className="min-w-0 flex-1 text-foreground text-destructive"
                  >
                    {displayId}
                  </data>
                  <Button
                    variant="ghost"
                    onClick={(): void => removeNgDisplayId(displayId)}
                    icon={<FiX size={16} />}
                  >
                    削除
                  </Button>
                </SettingRow>
              </li>
            ))}
          </ul>
        ) : (
          <SettingRow>
            <p className="text-muted-foreground">なし</p>
          </SettingRow>
        )}
      </SettingSection>

      <SettingSection title="NGスレ文" description="本文部分一致で非表示">
        <SettingRow>
          <Input
            type="text"
            placeholder="NGスレ文を入力"
            className="w-full"
            value={ngTitleInput}
            onChange={(e: InputChangeEvent): void =>
              setNgTitleInput(e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent): void => {
              if (e.key === "Enter") {
                handleAddNgTitle();
              }
            }}
          />
          <Button variant="primary" onClick={handleAddNgTitle}>
            追加
          </Button>
        </SettingRow>
        {ngTitles.length > 0 ? (
          <ul aria-label="NGスレ文" className="m-0 list-none space-y-2 p-0">
            {ngTitles.map((title) => (
              <li key={title}>
                <SettingRow>
                  <p className="min-w-0 flex-1 truncate text-foreground">
                    {title}
                  </p>
                  <Button
                    variant="ghost"
                    onClick={(): void => removeNgTitle(title)}
                    icon={<FiX size={16} />}
                  >
                    削除
                  </Button>
                </SettingRow>
              </li>
            ))}
          </ul>
        ) : (
          <SettingRow>
            <p className="text-muted-foreground">なし</p>
          </SettingRow>
        )}
      </SettingSection>

      <SettingSection title="NGワード" description="本文部分一致で非表示">
        <SettingRow>
          <Input
            type="text"
            placeholder="NGワードを入力"
            className="w-full"
            value={ngWordInput}
            onChange={(e: InputChangeEvent): void =>
              setNgWordInput(e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent): void => {
              if (e.key === "Enter") {
                handleAddNgWord();
              }
            }}
          />
          <Button variant="primary" onClick={handleAddNgWord}>
            追加
          </Button>
        </SettingRow>
        {ngWords.length > 0 ? (
          <ul aria-label="NGワード" className="m-0 list-none space-y-2 p-0">
            {ngWords.map((word) => (
              <li key={word}>
                <SettingRow>
                  <p className="min-w-0 flex-1 truncate text-foreground">
                    {word}
                  </p>
                  <Button
                    variant="ghost"
                    onClick={(): void => removeNgWord(word)}
                    icon={<FiX size={16} />}
                  >
                    削除
                  </Button>
                </SettingRow>
              </li>
            ))}
          </ul>
        ) : (
          <SettingRow>
            <p className="text-muted-foreground">なし</p>
          </SettingRow>
        )}
      </SettingSection>

      <SettingSection title="NG正規表現" description="正規表現パターンで非表示">
        <SettingRow>
          <Input
            type="text"
            placeholder="正規表現パターン（例: ^spam.*）"
            className="w-full"
            value={ngRegexInput}
            onChange={(e: InputChangeEvent): void =>
              setNgRegexInput(e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent): void => {
              if (e.key === "Enter") {
                handleAddNgRegex();
              }
            }}
          />
          <Button variant="primary" onClick={handleAddNgRegex}>
            追加
          </Button>
        </SettingRow>
        {ngRegexes.length > 0 ? (
          <ul aria-label="NG正規表現" className="m-0 list-none space-y-2 p-0">
            {ngRegexes.map((regex) => (
              <li key={regex}>
                <SettingRow>
                  <code className="min-w-0 flex-1 truncate text-sm text-foreground">
                    {regex}
                  </code>
                  <Button
                    variant="ghost"
                    onClick={(): void => removeNgRegex(regex)}
                    icon={<FiX size={16} />}
                  >
                    削除
                  </Button>
                </SettingRow>
              </li>
            ))}
          </ul>
        ) : (
          <SettingRow>
            <p className="text-muted-foreground">なし</p>
          </SettingRow>
        )}
      </SettingSection>

      <SettingSection title="NG画像" description="ハッシュから類似画像を非表示">
        <SettingRow>
          <Input
            type="text"
            placeholder="画像ハッシュ（64桁二進数 またはそれのURLセーフbase64エンコード）"
            className="w-full"
            value={ngImageInput}
            onChange={(e: InputChangeEvent): void =>
              setNgImageInput(e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent): void => {
              if (e.key === "Enter") {
                handleAddNgImage();
              }
            }}
          />
          <Button variant="primary" onClick={handleAddNgImage}>
            追加
          </Button>
        </SettingRow>
        <SettingRow>
          <Input
            type="text"
            placeholder="メモ（任意）"
            className="w-full"
            value={ngImageNoteInput}
            onChange={(e: InputChangeEvent): void =>
              setNgImageNoteInput(e.target.value)
            }
          />
          {ngImages.length > 0 && (
            <Button
              variant="ghost"
              onClick={(): void => {
                if (confirm("画像NGをすべて削除しますか？")) clearNgImages();
              }}
            >
              全削除
            </Button>
          )}
        </SettingRow>
        {ngImages.length > 0 ? (
          <ul aria-label="NG画像" className="m-0 list-none space-y-2 p-0">
            {ngImages.map((entry) => (
              <li key={entry.hash}>
                <SettingRow>
                  <figure className="flex min-w-0 flex-1 items-center gap-3">
                    <HashBitmap bits={entry.hash} />
                    <figcaption className="min-w-0 flex-1">
                      <code className="block text-foreground text-sm truncate">
                        {entry.hash}
                      </code>
                      {(entry.note || entry.addedAt > 0) && (
                        <p className="text-xs text-muted-foreground truncate">
                          {entry.note || "メモなし"}
                          {entry.addedAt > 0 &&
                            ` · ${formatJstDateTime(
                              instantFromEpochMilliseconds(entry.addedAt),
                              {
                                year: "numeric",
                                month: "numeric",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                              },
                            )}`}
                        </p>
                      )}
                    </figcaption>
                  </figure>
                  <Button
                    variant="ghost"
                    onClick={(): void => removeNgImage(entry.hash)}
                    icon={<FiX size={16} />}
                  >
                    削除
                  </Button>
                </SettingRow>
              </li>
            ))}
          </ul>
        ) : (
          <SettingRow>
            <p className="text-muted-foreground">なし</p>
          </SettingRow>
        )}
      </SettingSection>

      <SettingSection title="非表示スレ" description="非表示にしたスレの一覧">
        {hiddenThreadIds.length > 0 ? (
          <ul aria-label="非表示スレ" className="m-0 list-none space-y-2 p-0">
            {hiddenThreadIds.map((threadId) => (
              <li key={threadId}>
                <SettingRow>
                  <TextLink
                    to="/thread/$threadId"
                    params={{ threadId: String(threadId) }}
                    variant="primary"
                  >
                    {threadId}
                  </TextLink>
                  <Button
                    variant="ghost"
                    onClick={(): void => unhideThread(threadId)}
                    icon={<FiX size={16} />}
                  >
                    削除
                  </Button>
                </SettingRow>
              </li>
            ))}
          </ul>
        ) : (
          <SettingRow>
            <p className="text-muted-foreground">なし</p>
          </SettingRow>
        )}
      </SettingSection>

      <SettingSection title="NGフィルタ">
        <SettingRow label="NGフィルタを有効にする">
          <Toggle
            checked={enabled}
            onChange={setEnabled}
            aria-label="NGフィルタ切替"
          />
        </SettingRow>
        <SettingRow
          label="NGされたコンテンツを表示"
          description="ONにすると、NGされたスレッドやレスがラベル付きで表示されます"
        >
          <Toggle
            checked={showNgContent}
            onChange={setShowNgContent}
            aria-label="NGコンテンツ表示切替"
          />
        </SettingRow>
      </SettingSection>
    </>
  );
};
